import Typography from '@mui/material/Typography';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import { Markdown } from 'components/Markdown';
import { useHealthInsuranceContext } from 'contexts/HealthInsuranceContext';
import { Form, Tooltip, formModelGet, useExtendedReducer } from '@portal/ui-library';
import { RecursiveKeyOf } from '@portal/ui-library/dist/models';
import { partnerField } from 'models';
import {
  Application,
  Partner,
  Question,
  QuestionTypeEnum,
  SubQuestion
} from 'models/extension-generated';
import i18n from 'services/translation';
import { CustomMonthYearPicker } from './components/CustomMonthYearPicker';
import { useEffect, useRef } from 'react';
import { IconButton } from '@mui/material';

const { Input, YesNo, Radio } = Form.Components;

/**
 * Find all subquestions that depend on the current subquestion
 * @param subQuestion
 * @param subQuestions
 * @returns
 */

const getSubQuestionField = (
  partnerId: string,
  questionsId: string,
  detailId: string,
  attribute: string
): string => {
  return partnerField(
    partnerId,
    `applicationInformation.health[${questionsId}].details[${detailId}].${attribute}` as RecursiveKeyOf<Partner>
  );
};

const resolveRightOperand = (rightOperand: string, rightOperandType: QuestionTypeEnum) => {
  switch (rightOperandType) {
    case QuestionTypeEnum.CLOSED_QUESTION:
      return Boolean(parseInt(rightOperand));
    case QuestionTypeEnum.ENUMERATED_QUESTION:
      return rightOperand.split(',');
    default:
      return rightOperand;
  }
};

const evaluateCondition = (
  model: Application,
  leftOperand: string,
  rightOperand: string,
  questionId: string,
  partnerId: string,
  subQuestionId: string,
  rightOperandType: QuestionTypeEnum,
  questions: SubQuestion[]
) => {
  // get value of subquetion for this main question from data model for left and right operand
  const leftOperandAttributeKey = questions.find(({ id }) => id === leftOperand)?.attributeKey;

  if (!leftOperandAttributeKey) return <></>;
  const leftOperandValue = formModelGet(
    model,
    getSubQuestionField(partnerId, questionId, subQuestionId, leftOperandAttributeKey)
  );
  const rightOperandValue = resolveRightOperand(rightOperand, rightOperandType);

  if (!leftOperandValue || !rightOperandValue) return false;

  if (Array.isArray(rightOperandValue)) {
    return rightOperandValue.includes(leftOperandValue);
  }
  return leftOperandValue === rightOperandValue;
};

const RenderSubQuestion = ({
  subQuestion,
  componentKey,
  onChange,
  detailIndex
}: {
  subQuestion: SubQuestion;
  componentKey: string;
  onChange: (value: any) => void;
  detailIndex: number;
}) => {
  const { hasError, message } = Form.hooks.useFormComponent(componentKey);
  const diagnosisRef = useRef<HTMLInputElement>(null);
  const tooltipId = `tooltip-${componentKey}-${detailIndex}`;

  useEffect(() => {
    if (diagnosisRef.current && diagnosisRef.current.value === '') {
      diagnosisRef.current?.focus();
    }
  }, []);

  const handleOnChange = (value: any) => {
    onChange(value.value);
  };
  const handleOnInputChange = (value: any) => {
    onChange(value);
  };

  return (
    <div>
      {subQuestion.headline && (
        <>
          <div className="mb-m s:mb-s">
            <Typography component="h3" variant="bodyMDBold">
              {subQuestion.headline}
            </Typography>
          </div>
        </>
      )}

      <div className="flex flex-col s:flex-row s:items-center min-h-[55px] my-[24px]">
        <div className="pb-m s:pb-[0px] s:basis-5/12 s:grow-0 s:shrink-0 flex items-center">
          <Typography variant="bodySMRegular">{subQuestion.text}</Typography>
          <div>
            {subQuestion.tooltip && (
              <Tooltip
                tooltipContent={
                  <div className="max-w-[50vw] " id={tooltipId}>
                    <Typography variant="bodySMRegular">
                      <Markdown markdown={subQuestion.tooltip} />
                    </Typography>
                  </div>
                }
              >
                <IconButton
                  size="small"
                  className="text-highlight-80 mt-[2px]"
                  aria-describedby={tooltipId}
                  aria-label={`Information zu: ${subQuestion.text}`}
                >
                  <InfoIcon />
                </IconButton>
              </Tooltip>
            )}
          </div>
        </div>
        {subQuestion.questionType === QuestionTypeEnum.CLOSED_QUESTION && (
          <YesNo
            question={`${subQuestion.text}`}
            hideLabel
            name={`${subQuestion.text} ${detailIndex + 1}`}
            componentKey={componentKey}
            onChange={handleOnChange}
          />
        )}
        {subQuestion.questionType === QuestionTypeEnum.ENUMERATED_QUESTION && (
          <Radio
            question={`${subQuestion.text}`}
            hideLabel
            name={`Radio ${subQuestion.text} ${detailIndex + 1}`}
            componentKey={componentKey}
            options={(subQuestion.possibleValues || []).map((value) => {
              return {
                label: i18n.t(`common:wizard.healthSection.${value}`),
                value
              };
            })}
            onChange={handleOnChange}
            className="ml-[16px]"
          />
        )}
        {subQuestion.questionType === QuestionTypeEnum.TEXT_QUESTION && (
          <Input
            className="my-[24px]"
            ref={diagnosisRef}
            componentKey={componentKey}
            placeholder={subQuestion.hint}
            label={subQuestion.text}
            onChange={handleOnInputChange}
          />
        )}
        {subQuestion.questionType === QuestionTypeEnum.DATE_QUESTION && (
          <div className="basis-4/12 my-[24px]">
            <CustomMonthYearPicker
              onChange={(value) => {
                onChange(value);
              }}
              componentKey={componentKey}
            />
          </div>
        )}
      </div>
      {hasError && (
        <Typography component="div" variant="bodySMRegular" className="inline text-error-700">
          {message || 'hasError'}
        </Typography>
      )}
    </div>
  );
};

export const QuestionDetails = ({
  partnerId,
  questionId,
  detailId,
  subQuestion,
  question,
  mainQuestionValue,
  detailIndex
}: {
  partnerId: string;
  questionId: string;
  detailId: string;
  mainQuestionValue: boolean;
  subQuestion: SubQuestion;
  question: Question;
  detailIndex: number;
}) => {
  const { state, dispatch } = useHealthInsuranceContext();
  const { resetDetails } = useExtendedReducer(dispatch);
  const { rightOperand, leftOperand, rightOperandType, attributeKey = '' } = subQuestion;
  const hasOperandCondition = rightOperand && leftOperand && rightOperandType;
  const subQuestionKey = getSubQuestionField(partnerId, questionId, detailId, attributeKey);

  const resolvedCondition =
    hasOperandCondition &&
    evaluateCondition(
      state.model,
      leftOperand,
      rightOperand,
      questionId,
      partnerId,
      detailId,
      rightOperandType,
      question.subQuestions || []
    );
  const getDependencies = (subQuestion: SubQuestion, subQuestions: Array<SubQuestion>) => {
    const { id } = subQuestion;
    const dependantSubQuestions = subQuestions.filter(({ leftOperand }) => {
      return leftOperand === id;
    });

    return dependantSubQuestions;
  };

  const handleChangeHealthQuestionDetail = () => {
    const dependantSubQuestions = getDependencies(subQuestion, question.subQuestions || [])
      .map(({ attributeKey }) => attributeKey || '')
      .filter(Boolean);

    resetDetails({
      detailId,
      partnerId,
      questionId,
      attributes: dependantSubQuestions
    });
  };

  if (!mainQuestionValue) return <></>;

  return (
    <div>
      {!hasOperandCondition && (
        <RenderSubQuestion
          key={subQuestionKey}
          detailIndex={detailIndex}
          componentKey={subQuestionKey}
          subQuestion={subQuestion}
          onChange={handleChangeHealthQuestionDetail}
        />
      )}
      {resolvedCondition && (
        <RenderSubQuestion
          key={subQuestionKey}
          detailIndex={detailIndex}
          componentKey={subQuestionKey}
          subQuestion={subQuestion}
          onChange={handleChangeHealthQuestionDetail}
        />
      )}
    </div>
  );
};
